<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute, RouterLink } from 'vue-router'
import AuthLayout from './AuthLayout.vue'
import FieldLeaf  from '@/design/primitives/FieldLeaf.vue'
import ButtonSeed from '@/design/primitives/ButtonSeed.vue'
import { api, setToken } from '@/api/client.js'
import { EP } from '@/api/endpoints.js'
import { useToastStore } from '@/stores/toastStore'
import { useLegalLinks } from '@/router/legalLinks.js'

const router = useRouter()
const route  = useRoute()
const toast  = useToastStore()
const legalPaths = useLegalLinks()

/* ── Tab state ── */
const activeTab = ref(route.query.tab === 'signin' ? 'signin' : 'signup')

/* ── Sign-up form ── */
const suEmail    = ref('')
const suPassword = ref('')
const suConfirm  = ref('')
const suLoading  = ref(false)
const suError    = ref('')
const ageAccepted = ref(false)

/* Policy link labels — admin-controlled via Login Modal settings.
   Defaults match the regulatory phrasing; if the company switches
   "Terms of Use" → "Terms and Conditions" closer to release, only the
   admin field needs an update, no code change. */
const policyLabels = ref({
  terms: 'Terms of Use',
  privacy: 'Privacy Notice'
})
const ageText = ref('I confirm that I am 18 years old or older and I agree to the')

onMounted(async () => {
  try {
    const { data } = await api.get('/api/settings')
    if (data && typeof data === 'object') {
      if (data.loginTermsText)   policyLabels.value.terms   = data.loginTermsText
      if (data.loginPrivacyText) policyLabels.value.privacy = data.loginPrivacyText
      if (data.loginAgeText)     ageText.value              = data.loginAgeText
    }
  } catch {}
})

/* ── Sign-in form ── */
const siEmail    = ref('')
const siPassword = ref('')
const siLoading  = ref(false)
const siError    = ref('')

const steamUrl = computed(() =>
  `${import.meta.env.VITE_API_URL || ''}/api/auth/steam`
)

function switchTab(tab) {
  activeTab.value = tab
  suError.value = ''
  siError.value = ''
}

async function handleSignUp() {
  suError.value = ''
  if (!suEmail.value || !suPassword.value) {
    suError.value = 'EMAIL AND PASSWORD ARE REQUIRED.'
    return
  }
  if (suPassword.value !== suConfirm.value) {
    suError.value = 'PASSWORDS DO NOT MATCH.'
    return
  }
  if (suPassword.value.length < 6) {
    suError.value = 'PASSWORD MUST BE AT LEAST 6 CHARACTERS.'
    return
  }
  if (!ageAccepted.value) {
    suError.value = 'PLEASE CONFIRM YOU ARE 18+ AND AGREE TO THE POLICIES.'
    return
  }

  suLoading.value = true
  try {
    const res = await api.post(EP.authRegister, {
      email:    suEmail.value,
      password: suPassword.value
    })
    setToken(res.data.token)
    /* Account creation and SMTP delivery are separate outcomes. Never tell the
       user to wait for a message that the server could not hand to SMTP. */
    if (res.data.verificationEmailSent) {
      toast.show('Verification email sent — check your inbox to confirm your address.', { tone: 'success' })
    } else {
      toast.show('Account created, but the verification email could not be sent. Retry from your profile.', { tone: 'error' })
    }
    router.push('/profile')
  } catch (err) {
    suError.value = (err.message || 'REGISTRATION FAILED.').toUpperCase()
  } finally {
    suLoading.value = false
  }
}

async function handleSignIn() {
  siError.value = ''
  if (!siEmail.value || !siPassword.value) {
    siError.value = 'EMAIL AND PASSWORD ARE REQUIRED.'
    return
  }

  siLoading.value = true
  try {
    const res = await api.post(EP.authEmailLogin, {
      email:    siEmail.value,
      password: siPassword.value
    })
    setToken(res.data.token)
    const next = route.query.next
    router.push(next && next.startsWith('/') ? next : '/profile')
  } catch (err) {
    siError.value = (err.message || 'SIGN IN FAILED.').toUpperCase()
  } finally {
    siLoading.value = false
  }
}
</script>

<template>
  <AuthLayout kicker="EVOLUXU · ACCESS">

    <!-- Tab rail -->
    <div class="plate__tabs gold-rail" role="tablist">
      <button
        role="tab"
        :aria-selected="activeTab === 'signup'"
        class="tab"
        :class="{ 'tab--active': activeTab === 'signup' }"
        @click="switchTab('signup')"
      >
        CREATE ACCOUNT
      </button>
      <button
        role="tab"
        :aria-selected="activeTab === 'signin'"
        class="tab"
        :class="{ 'tab--active': activeTab === 'signin' }"
        @click="switchTab('signin')"
      >
        SIGN IN
      </button>
    </div>

    <!-- Steam link -->
    <a :href="steamUrl" class="plate__steam">
      <svg class="plate__steam-icon" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M11.979 0C5.678 0 .511 4.86.022 11.037l6.432 2.658c.545-.371 1.203-.59 1.912-.59.063 0 .125.004.188.006l2.861-4.142V8.91c0-2.495 2.028-4.524 4.524-4.524 2.494 0 4.524 2.031 4.524 4.527s-2.03 4.525-4.524 4.525h-.105l-4.076 2.911c0 .052.004.105.004.159 0 1.875-1.515 3.396-3.39 3.396-1.635 0-3.016-1.173-3.331-2.727L.436 15.27C1.862 20.307 6.486 24 11.979 24c6.627 0 11.999-5.373 11.999-12S18.606 0 11.979 0zM7.54 18.21l-1.473-.61c.262.543.714.999 1.314 1.25 1.297.539 2.793-.076 3.332-1.375.263-.63.264-1.319.005-1.949s-.75-1.121-1.377-1.383c-.624-.26-1.29-.249-1.878-.03l1.523.63c.956.4 1.409 1.497 1.009 2.452-.397.957-1.497 1.41-2.454 1.015h.001zm11.415-9.303c0-1.662-1.353-3.015-3.015-3.015-1.665 0-3.015 1.353-3.015 3.015 0 1.665 1.35 3.015 3.015 3.015 1.663 0 3.015-1.35 3.015-3.015zm-5.273-.005c0-1.252 1.013-2.266 2.265-2.266 1.249 0 2.266 1.014 2.266 2.266 0 1.251-1.017 2.265-2.266 2.265-1.252 0-2.265-1.014-2.265-2.265z"/>
      </svg>
      CONTINUE WITH STEAM
    </a>

    <!-- Or divider -->
    <div class="plate__divider" aria-hidden="true">
      <span class="plate__divider-line"></span>
      <span class="plate__divider-label">OR</span>
      <span class="plate__divider-line"></span>
    </div>

    <!-- CREATE ACCOUNT form -->
    <form
      v-if="activeTab === 'signup'"
      class="seal"
      @submit.prevent="handleSignUp"
      novalidate
    >
      <FieldLeaf v-model="suEmail"    label="EMAIL"            type="email"    placeholder="you@example.com"   required />
      <FieldLeaf v-model="suPassword" label="PASSWORD"         type="password" placeholder="Min. 6 characters" required />
      <FieldLeaf v-model="suConfirm"  label="CONFIRM PASSWORD" type="password" placeholder="Repeat password"   required />

      <label class="seal__age">
        <input v-model="ageAccepted" type="checkbox" class="seal__age-box" />
        <span class="seal__age-text">
          {{ ageText }}
          <a :href="legalPaths.terms" target="_blank" rel="noopener noreferrer" class="seal__age-link" @click.stop>{{ policyLabels.terms }}</a>
          and
          <a :href="legalPaths.privacy" target="_blank" rel="noopener noreferrer" class="seal__age-link" @click.stop>{{ policyLabels.privacy }}</a>
        </span>
      </label>

      <p v-if="suError" class="seal__error u-display u-allcaps" role="alert">{{ suError }}</p>

      <ButtonSeed type="submit" size="lg" :disabled="suLoading" class="seal__submit">
        {{ suLoading ? 'CREATING…' : 'CREATE ACCOUNT' }}
      </ButtonSeed>
    </form>

    <!-- SIGN IN form -->
    <form
      v-else
      class="seal"
      @submit.prevent="handleSignIn"
      novalidate
    >
      <FieldLeaf v-model="siEmail"    label="EMAIL"    type="email"    placeholder="you@example.com" required />
      <FieldLeaf v-model="siPassword" label="PASSWORD" type="password" placeholder="Your password"   required />

      <p v-if="siError" class="seal__error u-display u-allcaps" role="alert">{{ siError }}</p>

      <ButtonSeed type="submit" size="lg" :disabled="siLoading" class="seal__submit">
        {{ siLoading ? 'SIGNING IN…' : 'SIGN IN' }}
      </ButtonSeed>

      <RouterLink to="/forgot-password" class="seal__alt u-display u-allcaps">
        FORGOT PASSWORD?
      </RouterLink>
    </form>

  </AuthLayout>
</template>

<style scoped>
/* ── Tab rail ── */
.plate__tabs {
  display: flex;
  gap: 0;
  border-bottom: 1px solid var(--ash);
  margin-bottom: var(--sp-6);
}

.tab {
  flex: 1;
  padding: var(--sp-3) var(--sp-4);
  font-family: var(--font-display);
  font-size: var(--fs-12);
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--ink-mute);
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  border-radius: 0;
  transition: color var(--t-fast) var(--ease-road),
              border-color var(--t-fast) var(--ease-road);
}

.tab:hover { color: var(--ink-soft); }

.tab--active {
  color: var(--gold);
  border-bottom-color: var(--gold);
}

/* ── Steam link ── */
.plate__steam {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-3);
  width: 100%;
  padding: 13px var(--sp-6);
  border-radius: var(--r-sm);
  border: 1px solid var(--ash-strong);
  background: rgba(11, 10, 8, 0.45);
  font-family: var(--font-display);
  font-size: var(--fs-12);
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ink-soft);
  transition: border-color var(--t-fast) var(--ease-road),
              color var(--t-fast) var(--ease-road),
              background var(--t-fast) var(--ease-road);
  margin-bottom: var(--sp-3);
}

.plate__steam:hover {
  border-color: var(--gold);
  color: var(--gold);
  background: var(--gold-soft);
}

.plate__steam-icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  fill: currentColor;
}

/* ── Or divider ── */
.plate__divider {
  display: flex;
  align-items: center;
  gap: var(--sp-4);
  margin-bottom: var(--sp-6);
}

.plate__divider-line {
  flex: 1;
  height: 1px;
  background: var(--gold);
  opacity: 0.18;
}

.plate__divider-label {
  font-family: var(--font-display);
  font-size: var(--fs-12);
  letter-spacing: 0.14em;
  color: var(--ink-mute);
  text-transform: uppercase;
}

/* ── Form seal ── */
.seal {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

.seal__submit {
  width: 100%;
  margin-top: var(--sp-2);
}

.seal__error {
  font-size: var(--fs-12);
  color: var(--ember);
  margin: 0;
  letter-spacing: 0.08em;
}

.seal__age {
  display: flex;
  align-items: flex-start;
  gap: var(--sp-3);
  font-size: var(--fs-12);
  color: var(--ink-soft);
  line-height: 1.5;
  cursor: pointer;
}
.seal__age-box {
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  margin-top: 3px;
  accent-color: var(--gold);
  cursor: pointer;
}
.seal__age-text { flex: 1 1 auto; }
.seal__age-link {
  color: var(--gold);
  text-decoration: none;
  border-bottom: 1px dotted rgba(229, 199, 107, 0.4);
}
.seal__age-link:hover {
  border-bottom-color: var(--gold);
}

.seal__alt {
  display: block;
  text-align: center;
  font-size: var(--fs-12);
  letter-spacing: 0.12em;
  color: var(--ink-mute);
  transition: color var(--t-fast) var(--ease-road);
}

.seal__alt:hover { color: var(--gold); }
</style>
